// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import React, {
  useMemo, useRef, useState,
  useEffect, useCallback
} from 'react';

import EmptyList from './empty';
import Topbar from '../tabView/topbar';
import { TEXTS } from '../../constants';
import ItemCard from '../../atoms/card/item';
import Dropdown from '../../atoms/dropdown/list';
import ScrollableContainer from '../../atoms/container/scrollable';
import { fetchBackpackItems } from '../../repositories/store/thunks';
import { BackpackItem, IScrollableContainer } from '../../common/types';
import { selectConfig, selectUserBackpack } from '../../repositories/store/slices';

// interfaces / types / consts
type SortBy = 'name' | 'type';
type Filter = 'all' | 'resource' | 'chest';
type ItemCB = (item?: BackpackItem) => void;

interface Props {
  defaultSelected?: string;
  selectedItem?: BackpackItem;
  itemClickedCB?: ItemCB;
  filterChangedCB?: ItemCB;
}

// exports
export default function ItemList(props: Props): JSX.Element {
  const {
    selectedItem, defaultSelected, itemClickedCB, filterChangedCB
  } = props;
  // hooks configuration
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const { items } = useSelector(selectConfig);
  const containerRef = useRef<IScrollableContainer>(null);
  const [sortOrder, setSortOrder] = useState<SortBy>('name');
  const [itemsFilter, setItemsFilter] = useState<Filter>('all');
  const { items: backpackItems, fetchStatus } = useSelector(selectUserBackpack);

  // private methods
  const isTypeMatched = useCallback((item: BackpackItem) => (item.type === itemsFilter), [itemsFilter]);
  const handleCB = (item?: BackpackItem, cb?: ItemCB): void => { if (cb) { cb(item); } };
  const filteredItems = useMemo(() => {
    const backpackData: BackpackItem[] = (fetchStatus === 'success')
      ? Object.keys(backpackItems).map((id) => items[id]).filter((item) => item.visible) : [];
    const filtered = (itemsFilter === 'all') ? backpackData : backpackData.filter((item) => (isTypeMatched(item)));
    if (sortOrder === 'name') {
      filtered.sort((a, b) => (a.name < b.name ? -1 : 0));
    } else if (sortOrder === 'type') {
      filtered.sort((a, b) => (a.type < b.type ? -1 : 0));
    } else {
      filtered.sort((a, b) => (a.id < b.id ? -1 : 0));
    }
    return filtered;
  }, [fetchStatus, backpackItems, itemsFilter, sortOrder, items, isTypeMatched]);
  const validItems = Boolean(filteredItems.length);
  const sortOptions = [{ key: 'Name', onClick: () => { setSortOrder('name'); } }];
  if (itemsFilter === 'all') { sortOptions.push({ key: 'Type', onClick: () => { setSortOrder('type'); } }); }

  useEffect(() => { // componentDidMount
    if (fetchStatus === 'idle') { dispatch(fetchBackpackItems() as unknown as AnyAction); }
    const defaultSelectedItem = defaultSelected ? filteredItems.find((item) => item.id === defaultSelected) : filteredItems[0];
    if (isInitialMount.current && validItems) {
      isInitialMount.current = false;
      handleCB(defaultSelectedItem!, filterChangedCB);
    } else if (!isInitialMount.current && itemsFilter !== 'all' && selectedItem && !isTypeMatched(selectedItem)) {
      handleCB(filteredItems[0], filterChangedCB);
    } else if (!isInitialMount.current && !selectedItem && Boolean(filteredItems.length)) {
      handleCB(defaultSelectedItem!, filterChangedCB);
    }
  });

  const handleTabClick = (filter: Filter): void => {
    setItemsFilter(filter);
    containerRef.current?.resetScroll();
  };

  // render
  return (
    <div className="item-container">
      <Topbar
        tabs={[
          { key: 'all', onClick: () => handleTabClick('all') },
          { key: 'resources', onClick: () => handleTabClick('resource') },
          { key: 'chests', onClick: () => handleTabClick('chest') }
        ]}
      />
      <ScrollableContainer ref={containerRef} fixed={<Dropdown items={sortOptions} />}>
        {!validItems ? <EmptyList text={fetchStatus === 'success' ? TEXTS.LIST_ITEMS_EMPTY : TEXTS.LIST_LOADING} /> : (
          <div className="list">
            {filteredItems.map((item) => (
              <ItemCard
                item={item}
                key={item.id}
                disabled={!itemClickedCB}
                selected={selectedItem?.id === item.id}
                onClicked={() => handleCB(item, itemClickedCB)}
              />
            ))}
          </div>
        )}
      </ScrollableContainer>
    </div>
  );
}

// default props
ItemList.defaultProps = {
  defaultSelected: '',
  selectedItem: undefined,
  itemClickedCB: undefined,
  filterChangedCB: undefined
};
